// Castintech dashboard — MicroFX micro-interactions library (TS port).
// Aşama D2 (2026-05-24) — vault MicroFX library'sinin (vault/templates/design-system/animations-library.js)
// TypeScript adaptasyonu. Dashboard runtime'ında kullanılır.
//
// 6 standart micro-interaction:
//   - hoverLift   : card hover lift effect (translateY + shadow)
//   - focusRing   : explicit focus halo (CSS :focus-visible global ile uyumlu)
//   - tapFeedback : button press scale-down (mobile + desktop)
//   - scrollReveal: viewport'a giren element fade-up reveal
//   - skeleton    : data fetch sırasında shimmer placeholder
//   - pageTransition: view switch sırasında subtle cross-fade
//
// Tüm animation'lar prefers-reduced-motion saygısı taşır — `_canAnimate()`
// helper ile her fn başında check edilir. CSS @media globalde 0.01ms duration,
// JS tarafında da motion bypass için early return.
//
// Data-attribute API (declarative bind):
//   <div data-reveal>...</div>                  → scrollReveal
//   <button data-tap-feedback>...</button>      → tapFeedback
//   <a data-hover-lift>...</a>                  → hoverLift
//   <input data-focus-ring>...</input>          → focusRing (CSS :focus-visible yedek)
//
// initMicroFX() call'i tüm marked element'leri auto-bind eder. main.ts boot'tan çağrılır.

// ─────────────────────────────────────────────────────────────────────────────
// Helpers

/**
 * prefers-reduced-motion: reduce set ise animation skip.
 * OS-level "Reduce motion" ayarı veya kullanıcı tercihinden gelir.
 */
function _canAnimate(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. hoverLift — Card hover: translateY(-N) + shadow

export interface HoverLiftOptions {
  lift?: number;   // px, default 4
  shadow?: string; // CSS box-shadow, default 0 8px 24px rgba(0,0,0,0.18)
  duration?: number; // ms, default 240
}

export function hoverLift(el: HTMLElement, opts: HoverLiftOptions = {}): void {
  const lift = opts.lift ?? 4;
  const shadow = opts.shadow ?? '0 8px 24px rgba(0,0,0,0.18)';
  const duration = opts.duration ?? 240;
  el.style.transition = `transform ${duration}ms cubic-bezier(0.22,1,0.36,1), box-shadow ${duration}ms`;
  el.addEventListener('mouseenter', () => {
    if (!_canAnimate()) return;
    el.style.transform = `translateY(-${lift}px)`;
    el.style.boxShadow = shadow;
  });
  el.addEventListener('mouseleave', () => {
    el.style.transform = '';
    el.style.boxShadow = '';
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. focusRing — Explicit focus halo (CSS :focus-visible global yedek)

export function focusRing(el: HTMLElement, color?: string): void {
  const accent = color ?? 'var(--indigo, #6366f1)';
  el.addEventListener('focus', () => {
    el.style.outline = `2px solid ${accent}`;
    el.style.outlineOffset = '2px';
  });
  el.addEventListener('blur', () => {
    el.style.outline = '';
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. tapFeedback — Button press scale-down (mobile + desktop)

export function tapFeedback(el: HTMLElement): void {
  el.addEventListener('pointerdown', () => {
    if (!_canAnimate()) return;
    el.style.transition = 'transform 80ms';
    el.style.transform = 'scale(0.97)';
  });
  const reset = () => { el.style.transform = ''; };
  el.addEventListener('pointerup', reset);
  el.addEventListener('pointerleave', reset);
  el.addEventListener('pointercancel', reset);
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. scrollReveal — IntersectionObserver fade-up reveal

export interface ScrollRevealOptions {
  threshold?: number;     // 0-1, default 0.15
  rootMargin?: string;    // CSS margin, default 0px
  once?: boolean;         // true: tek seferlik (default), false: her enter/exit
}

export function scrollReveal(
  selector: string = '[data-reveal]',
  opts: ScrollRevealOptions = {},
): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null;
  const threshold = opts.threshold ?? 0.15;
  const rootMargin = opts.rootMargin ?? '0px';
  const once = opts.once ?? true;

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('is-revealed');
        if (once) io.unobserve(e.target);
      } else if (!once) {
        e.target.classList.remove('is-revealed');
      }
    });
  }, { threshold, rootMargin });

  document.querySelectorAll(selector).forEach((el) => io.observe(el));
  return io;
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. skeleton — Shimmer placeholder for loading state

export function skeleton(el: HTMLElement): void {
  el.classList.add('skeleton');
  // CSS class .skeleton ile shimmer (dashboard.css'e D5'te eklenir)
  // Şimdilik inline fallback:
  if (!_canAnimate()) {
    // Reduced motion: shimmer yerine statik placeholder
    el.style.background = 'var(--card2, #111126)';
    return;
  }
  el.style.background =
    'linear-gradient(90deg, var(--card, #0d0d20) 0%, var(--card2, #111126) 50%, var(--card, #0d0d20) 100%)';
  el.style.backgroundSize = '200% 100%';
  el.style.animation = 'skeleton-shimmer 1.4s linear infinite';
  // Keyframes runtime'da inject (CSS'te yoksa idempotent ekle)
  if (!document.getElementById('_mfx-skeleton-kf')) {
    const s = document.createElement('style');
    s.id = '_mfx-skeleton-kf';
    s.textContent = '@keyframes skeleton-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }';
    document.head.appendChild(s);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. pageTransition — Subtle cross-fade for view switch
// (Toast separate — js/ui/toast.ts'de var; burası duplicate değil)

export interface PageTransitionOptions {
  duration?: number; // ms, default 180
}

export function pageTransition(
  oldView: HTMLElement | null,
  newView: HTMLElement,
  opts: PageTransitionOptions = {},
): void {
  const duration = opts.duration ?? 180;
  if (!_canAnimate()) {
    // Reduced motion: instant swap
    if (oldView) oldView.style.display = 'none';
    newView.style.display = '';
    return;
  }
  if (oldView) {
    oldView.style.transition = `opacity ${duration}ms`;
    oldView.style.opacity = '0';
    setTimeout(() => {
      oldView.style.display = 'none';
      oldView.style.opacity = '';
      oldView.style.transition = '';
    }, duration);
  }
  newView.style.opacity = '0';
  newView.style.display = '';
  newView.style.transition = `opacity ${duration}ms`;
  requestAnimationFrame(() => {
    newView.style.opacity = '1';
  });
  setTimeout(() => {
    newView.style.opacity = '';
    newView.style.transition = '';
  }, duration);
}

// ─────────────────────────────────────────────────────────────────────────────
// initMicroFX — Auto-bind data-attribute API

/**
 * MicroFX boot — main.ts'den çağrılır (DOMContentLoaded sonrası).
 *
 * Otomatik bağlanan attribute'lar:
 *   - [data-reveal]        → scrollReveal
 *   - [data-hover-lift]    → hoverLift
 *   - [data-tap-feedback]  → tapFeedback
 *   - [data-focus-ring]    → focusRing (genelde :focus-visible global yeterli)
 *
 * @returns Bound element sayısı (debug için)
 */
export function initMicroFX(): { reveal: number; hover: number; tap: number; focus: number } {
  let revealCount = 0;
  let hoverCount = 0;
  let tapCount = 0;
  let focusCount = 0;

  // 1. scrollReveal — data-reveal
  const io = scrollReveal('[data-reveal]');
  if (io) {
    revealCount = document.querySelectorAll('[data-reveal]').length;
  }

  // 2. hoverLift — data-hover-lift
  document.querySelectorAll<HTMLElement>('[data-hover-lift]').forEach((el) => {
    hoverLift(el);
    hoverCount++;
  });

  // 3. tapFeedback — data-tap-feedback
  document.querySelectorAll<HTMLElement>('[data-tap-feedback]').forEach((el) => {
    tapFeedback(el);
    tapCount++;
  });

  // 4. focusRing — data-focus-ring (sadece explicit istenirse)
  document.querySelectorAll<HTMLElement>('[data-focus-ring]').forEach((el) => {
    focusRing(el);
    focusCount++;
  });

  return { reveal: revealCount, hover: hoverCount, tap: tapCount, focus: focusCount };
}

// ─────────────────────────────────────────────────────────────────────────────
// Default export — convenience

export default {
  hoverLift,
  focusRing,
  tapFeedback,
  scrollReveal,
  skeleton,
  pageTransition,
  initMicroFX,
};
